<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.name" placeholder="自由职业者名称" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.idNo" placeholder="身份证号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.phone" placeholder="通讯手机号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.signUpType"
          filterable
          placeholder="报名方式"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="registrationMethodLabel[ERegistrationMethod.MINI]" :value="ERegistrationMethod.MINI" />
          <el-option :label="registrationMethodLabel[ERegistrationMethod.ASSIGN]" :value="ERegistrationMethod.ASSIGN" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.authStatus"
          filterable
          placeholder="实名状态"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="authStatusLabel[EAuthStatus.Yes]" :value="EAuthStatus.Yes" />
          <el-option :label="authStatusLabel[EAuthStatus.No]" :value="EAuthStatus.No" />
        </el-select>
      </search-col>
      <search-col v-if="taxSourceType != ETaxSourceType.Hainan">
        <el-select
          v-model="params.faceAuthStatus"
          filterable
          placeholder="人脸核验"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="faceAuthLabel[EFaceAuth.VERIFIED]" :value="EFaceAuth.VERIFIED" />
          <el-option :label="faceAuthLabel[EFaceAuth.UNVERIFIED]" :value="EFaceAuth.UNVERIFIED" />
        </el-select>
      </search-col>
      <search-col v-if="hnInvoiceType == 'OFFLINE' && taxSourceType == ETaxSourceType.Hainan && (from == 'saas' || from == 'company')">
        <el-select 
          v-model="params.inHnAuthTime" 
          placeholder="本人授权有效期" 
          clearable 
          filterable
        >
          <el-option 
            label="授权有效期内" 
            :value="true"
          />
          <el-option 
            label="授权过期" 
            :value="false"
          />
        </el-select>
      </search-col>
      <search-col v-if="taxSourceType == ETaxSourceType.Hainan && hnInvoiceType == 'ONLINE'">
        <el-select
          v-model="params.hnTaxSourceStatus"
          filterable
          placeholder="海南关系绑定"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerTaxSourceLocationLabel[ETaxSourceLocation.YES]" :value="ETaxSourceLocation.YES" />
          <el-option :label="freelancerTaxSourceLocationLabel[ETaxSourceLocation.NO]" :value="ETaxSourceLocation.NO" />
        </el-select>
      </search-col>
      <search-col v-if="taxSourceType == ETaxSourceType.Hainan">
        <el-select
          v-model="params.hnTaxStatus"
          filterable
          placeholder="税务登记"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerTaxSourceRegisterLabel[ETaxSourceRegister.YES]" :value="ETaxSourceRegister.YES" />
          <el-option :label="freelancerTaxSourceRegisterLabel[ETaxSourceRegister.NO]" :value="ETaxSourceRegister.NO" />
        </el-select>
      </search-col>
      <search-col v-if="taxSourceType == ETaxSourceType.Hainan">
        <el-select
          v-model="params.contractSignatureStatus"
          filterable
          placeholder="合同签署状态"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerContractStatusLabel[EFreelancerContractStatus.SIGNED]" :value="EFreelancerContractStatus.SIGNED" />
          <el-option :label="freelancerContractStatusLabel[EFreelancerContractStatus.UNSIGNED]" :value="EFreelancerContractStatus.UNSIGNED" />
        </el-select>
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 非搜索区域的额外按钮 -->
    <div>
      <el-button 
        v-if="from !== 'agent'"
        v-permission="['customer:duty:import']"
        type="primary"
        class="mb-[20px]"
        @click="goImport"
      >
        导入自由职业者
      </el-button>
      <el-button v-if="from == 'saas' || from == 'company'" v-permission="['crowd:customer:sms:send']" type="primary" class="mb-[20px]" :disabled="selectFreelancers.length<1" @click="handleSendMess">
        发送短信
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table ref="registrationTable" v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" @selection-change="handleSelectionChange">
      <el-table-column align="center" type="selection" :min-width="55" />
      <el-table-column align="left" prop="name" label="自由职业者名称" :min-width="130" />
      <el-table-column align="left" prop="idNo" label="身份证号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="phone" label="通讯手机号" :min-width="120" />
      <el-table-column align="left" prop="signUpType" label="报名方式" :min-width="120">
        <template #default="scope">
          {{ registrationMethodLabel[scope.row.signUpType] }}
        </template>
      </el-table-column>
      <el-table-column align="center" prop="authStatus" label="实名状态" :min-width="120">
        <template #default="scope">
          <custom-tag :label="authStatusLabel[scope.row.authStatus]" :type="authStatusStyle[scope.row.authStatus]" />
        </template>
      </el-table-column>
      <el-table-column v-if="taxSourceType != ETaxSourceType.Hainan" align="center" prop="faceAuthStatus" label="人脸核验" :min-width="120">
        <template #default="scope">
          <custom-tag :label="faceAuthLabel[scope.row.faceAuthStatus]" :type="faceAuthStyle[scope.row.faceAuthStatus]" />
        </template>
      </el-table-column>
      <el-table-column v-if="hnInvoiceType == 'OFFLINE' && taxSourceType == ETaxSourceType.Hainan && (from == 'saas' || from == 'company')" align="center" prop="hnAuthRemainingDate" label="本人授权有效期" :min-width="130">
        <template #default="scope">
          <custom-tag :icon="getAuthTimeIcon(scope.row.hnAuthRemainingDate)" :label="scope.row.hnAuthRemainingDate" :type="getAuthTimeType(scope.row.hnAuthRemainingDate) " />
        </template>
      </el-table-column>
      <el-table-column v-if="showHnTaxSourceStatusCol && taxSourceType == ETaxSourceType.Hainan && hnInvoiceType == 'ONLINE'" align="center" prop="hnTaxSourceStatus" label="海南关系绑定" :min-width="120">
        <template #default="scope">
          <custom-tag :label="freelancerTaxSourceLocationLabel[scope.row.hnTaxSourceStatus]" :type="freelancerTaxSourceLocationStyle[scope.row.hnTaxSourceStatus]" />
        </template>
      </el-table-column>
      <el-table-column v-if="showHnTaxSourceStatusCol && taxSourceType == ETaxSourceType.Hainan" align="center" prop="hnTaxStatus" label="税务登记" :min-width="120">
        <template #default="scope">
          <custom-tag :label="freelancerTaxSourceRegisterLabel[scope.row.hnTaxStatus]" :type="freelancerTaxSourceRegisterStyle[scope.row.hnTaxStatus]" />
        </template>
      </el-table-column>
      <el-table-column v-if="taxSourceType == ETaxSourceType.Hainan" align="center" prop="contractSignatureStatus" label="合同签署状态" :min-width="110" show-overflow-tooltip>
        <template #default="scope">
          <div v-if="scope.row.contractSignatureStatus == null">
            --
          </div>
          <el-popover
            v-else
            placement="bottom-start"
            width="375"
            trigger="click"
          >
            <template #reference>
              <el-link type="primary" :underline="false" @click="handleClickSearch(scope.row)">
                {{ agreementStatusLabel[scope.row.contractSignatureStatus] }}
              </el-link>
            </template>
            <ul>
              <li v-for="item in currentRowContractList" :key="item.id" class="border-solid border-b border-[#EBEEF5]">
                <div class="w-[100%] h-6 mt-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  {{ tempBusinessConfig[item.businessType] }}{{ item.contractStatus == EAcceptPackageContractStatus.SIGNED ? '（已签署）' : '（未签署）' }}
                </div>
                <div class="flex justify-end items-center w-[100%] h-8">
                  <div class="w-[240px] mr-[auto] font-bold whitespace-nowrap overflow-hidden text-ellipsis">
                    {{ item.contractSubjectName }}
                  </div>
                  <div v-if="item.contractStatus == EAcceptPackageContractStatus.NOT_GENERATED">
                    合同未生成
                  </div>
                  <div v-else>
                    <el-link type="primary" :underline="false" class="mr-2" @click="previewContract(item)">
                      查看
                    </el-link>
                    <el-link type="primary" :underline="false" @click="downloadContract(item)">
                      下载
                    </el-link>
                  </div>
                </div>
              </li>
            </ul>
          </el-popover>
        </template>
      </el-table-column>

      <el-table-column
        v-if="(from == 'saas' || from == 'company') && buttons.length > 0 && taxSourceType == ETaxSourceType.Hainan"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        width="160"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <ChangeValidityPeriod 
      :visible="showChangeValidityPeriod"
      @close="()=>{ showChangeValidityPeriod = false }"
      @confirm="handleConfirmChangeValidityPeriod"
    />
  </content-container>
</template>

<script setup lang="ts">
import { computed, PropType, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, TableRowButtons, CustomTag } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  signSendSmsFreelancer,
  useUserStore,
  queryTaxSourceList,
  queryDutyAcceptPackageContractView,
  queryContractSignPreview,
  queryContractSignDownload,
  queryFreelanceSignUpPage,
  updateFreelancerHnAuthDate,
} from '@core/main';
import { useRoute, useRouter } from 'vue-router';
import {
  freelancerContractStatusLabel,
  registrationMethodLabel,
  ERegistrationMethod,
  authStatusLabel,
  authStatusStyle,
  EAuthStatus,
  freelancerTaxSourceLocationLabel,
  ETaxSourceLocation,
  EFreelancerContractStatus,
  freelancerTaxSourceLocationStyle,
  IDutyAcceptPackageContractViewItem,
  EAcceptPackageContractStatus,
  tempBusinessConfig,
  agreementStatusLabel,
  ETaxSourceType,
  EFreelancerSmsType,
  freelancerTaxSourceRegisterLabel,
  freelancerTaxSourceRegisterStyle,
  ETaxSourceRegister,
  faceAuthLabel,
  faceAuthStyle,
  EFaceAuth,
  IDutySignUpUser,
  IDutySignUpUserParams,
} from '@core/main/types';
import { getAuthTimeIcon, getAuthTimeType } from '@core/tools';
import ChangeValidityPeriod from './dialog/ChangeValidityPeriod.vue';
import { ESystemType } from '@core/api';

defineOptions({ name: 'RegistrationPersonnel' });

const props = defineProps({
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const defaultPageSize = 12;
const params = reactive<IDutySignUpUserParams>({
  page: 1,
  size: defaultPageSize,
});
const total = ref(0);
const tableData = ref<IDutySignUpUser[]>([]);
const registrationTable = ref();
const selectFreelancers = ref([]);
const showHnTaxSourceStatusCol = ref(false);

const { state: userState } = useUserStore();
const companyId = computed(() => userState.identity.companyId || Number(route.query.companyId));
const taxSourceType = computed(() => route.query && Number(route.query.taxSourceType) as ETaxSourceType || undefined);

const currentRow = ref<IDutySignUpUser>({} as IDutySignUpUser);

const currentRowContractList = ref<IDutyAcceptPackageContractViewItem[]>([]);
const hnInvoiceType = computed(() => String(route.query.hnInvoiceType));

const showChangeValidityPeriod = ref(false);

const { buttons } = useTblPermissionButtons([
  {
    name: '发送短信',
    code: 'crowd:customer:sms:send',
    action: handleSendMess,
  },
  {
    name: '修改有效期',
    code: 'crowd:customer:sms:send',
    action: handleChangeValidityPeriod,
    systemType: [ESystemType.SAAS],
  },
]);

async function goImport() {
  router.push({
    name: 'PersonnelImport',
    query: { dutyId: route.query.taskId },
  });
}

async function handleSendMess(row?: any) {
  if (row) {
    currentRow.value = row;
  } else {
    currentRow.value = {} as IDutySignUpUser;
  }
  loading.value = true;
  try {
    if (currentRow.value.idNo) {
      await signSendSmsFreelancer({
        idNos: [currentRow.value.idNo],
        companyId: companyId.value,
        sendType: hnInvoiceType.value == 'OFFLINE' ? EFreelancerSmsType.LIVENESS_AUTH : EFreelancerSmsType.HN_AUTH,
        dutyId: Number(route.query.taskId),
      });
    } else {
      const details = registrationTable.value.getSelectionRows();

      selectFreelancers.value = details.map((v) => v.idNo);
      await signSendSmsFreelancer({
        idNos: selectFreelancers.value,
        companyId: companyId.value,
        sendType: hnInvoiceType.value == 'OFFLINE' ? EFreelancerSmsType.LIVENESS_AUTH : EFreelancerSmsType.HN_AUTH,
        dutyId: Number(route.query.taskId),
      });
    }

    ElMessage.success('发送成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  loading.value = false;
}

function handleSelectionChange(val: any) {
  selectFreelancers.value = val.map((v) => v.idNo);
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

async function fetchTaxSource() {
  if (props.from == 'company') {
    try {
      const data = await queryTaxSourceList(companyId.value);

      if (data.length) {
        showHnTaxSourceStatusCol.value = true;
      }
    } catch (error) {
      if ((error as Error).message) ElMessage.error((error as Error).message);
    }
  } else if (props.from == 'saas') {
    showHnTaxSourceStatusCol.value = true;
  } else if (props.from == 'agent') {
    showHnTaxSourceStatusCol.value = true;
  }
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryFreelanceSignUpPage({
      ...params,
      id: Number(route.query.taskId),
    });

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

async function init() {
  await fetchData();
  await fetchTaxSource();
}

async function handleClickSearch(row) {
  try {
    const res = await queryDutyAcceptPackageContractView(row.dutyId, row.idNo);

    currentRowContractList.value = res;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function previewContract(row) {
  try {
    const res = await queryContractSignPreview(row.id);

    window.open(res);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function downloadContract(row) {
  try {
    const url = await queryContractSignDownload({
      page: 1,
      size: 10,
      ids: [row.id],
    });

    window.open(url);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function handleChangeValidityPeriod(row) {
  currentRow.value = row;
  showChangeValidityPeriod.value = true;
}

async function handleConfirmChangeValidityPeriod(val) {
  showChangeValidityPeriod.value = false;
  try {
    await updateFreelancerHnAuthDate({
      idNo: currentRow.value.idNo,
      hnAuthDate: val,
    });
    ElMessage.success('修改成功');
    fetchData();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

init();
</script>
